import { useEffect } from 'react';

export interface PageMetadata {
  title?: string;
  description?: string;
  keywords?: string;
}

/**
 * Hook to set page metadata (title, description, etc.)
 *
 * @example
 * ```tsx
 * usePageMetadata({
 *   title: 'Login - ESP Application',
 *   description: 'Sign in to your account'
 * });
 * ```
 */
export function usePageMetadata(metadata: PageMetadata) {
  useEffect(() => {
    const defaultTitle = 'ESP Application';
    const title = metadata.title
      ? `${metadata.title} | ${defaultTitle}`
      : defaultTitle;

    // Set document title
    document.title = title;

    // Set meta description if provided
    if (metadata.description) {
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', metadata.description);
    }

    // Set meta keywords if provided
    if (metadata.keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', metadata.keywords);
    }

    // Cleanup function to restore default title if needed
    return () => {
      // Optionally restore default title on unmount
      // document.title = defaultTitle;
    };
  }, [metadata.title, metadata.description, metadata.keywords]);
}
